import { useCallback, useEffect } from 'react';
import { useCrossTabEventsListener, NameSpaces, sendCrossTabEvent } from 'modules/EventsManager';
import { useRecorderVoiceCommandEventsListener } from '../hooks/useRecorderVoiceCommandEventsListener';
import { useGamepadBindings } from 'utils/gamepad';
import { useWebHIDBindings } from 'domains/reporter/WebHID/useWebHIDBindings';
import { GamepadActionId } from 'generated/graphql';
import { FocusModeToggleMethod, useFocusMode } from 'hooks/useFocusMode';
import { useDiscardReport } from 'hooks/useDiscardReport';
import { useUnsignedReportWarningDialog } from '../UnsignedReportWarningDialog/useUnsignedReportWarningDialog';
import { useReportExitActionData } from 'domains/reporter/RichTextEditor/analytics/useReportExitActionData';
import type { telemetryContextFor } from 'modules/Apollo/types';
import {
  ReportExitAction,
  ReportExitActionInputMethod,
} from 'domains/reporter/RichTextEditor/analytics/performance';
import { RESPONSE_EVENTS as EXTENSION_RESPONSE_EVENTS } from '../../../extension/constants';
import {
  addExtensionListener,
  dispatchExtensionEvent,
  focusEvent,
  removeExtensionListener,
} from '../../../extension/extensionEventCreators';
import {
  isLinkCasesBlockedExtensionShortcutDetail,
  isLinkCasesBlockedShortcutAction,
  isLinkCasesBlockedVoiceAction,
} from '../LinkCases/utils';
import { ReportStatus } from '../state';
import type { ReportStatuses } from '../state';
import { SubmitType, type OnSubmitParams } from './types';
import type { useAutoSaveReport } from './useAutoSaveReport';

type DiscardReport = ReturnType<typeof useDiscardReport>;
type FocusMode = ReturnType<typeof useFocusMode>;
type UnsignedDialog = ReturnType<typeof useUnsignedReportWarningDialog>;
type ReportExitActionData = ReturnType<typeof useReportExitActionData>;
type AutoSaveReport = ReturnType<typeof useAutoSaveReport>;

/**
 * Wires the reporter's action-input bindings — voice command, keyboard shortcut, browser-extension
 * custom event, and gamepad/WebHID — to the submit/draft/discard/impression/focus commands. Pure
 * side effects: it registers listeners and returns nothing. `handleDraftAction` (used only by the
 * voice and keyboard bindings) lives here too.
 */
export interface UseReporterInputBindingsParams {
  status: ReportStatuses;
  isLinkCasesMutating: boolean;
  isPrelimSubmissionEnabled: boolean;
  isAutoImpressionsEnabled: boolean;
  isFocusModeAllowed: boolean;
  unsignedReportDialogOpen: boolean;
  discardReportOpen: boolean;
  handleSubmit: (options?: OnSubmitParams) => Promise<void>;
  handleSaveDraft: (options?: OnSubmitParams) => Promise<void>;
  handleCancelAddAddendum: (options?: OnSubmitParams) => Promise<void>;
  handleDiscardReport: DiscardReport['handleDiscardReport'];
  handleOpenDiscardReportModal: DiscardReport['handleOpenDiscardReportModal'];
  handleKeepDraft: DiscardReport['handleKeepDraft'];
  toggleFocusMode: FocusMode['toggleFocusMode'];
  onDialogActionSuccess: UnsignedDialog['onDialogActionSuccess'];
  onDialogActionError: UnsignedDialog['onDialogActionError'];
  measureReportExitActionStartWithMetadata: ReportExitActionData['measureReportExitActionStartWithMetadata'];
  fieldsUpdateDebounced: AutoSaveReport['fieldsUpdateDebounced'];
  apolloQueryContext: ReturnType<typeof telemetryContextFor>;
}

export const useReporterInputBindings = ({
  status,
  isLinkCasesMutating,
  isPrelimSubmissionEnabled,
  isAutoImpressionsEnabled,
  isFocusModeAllowed,
  unsignedReportDialogOpen,
  discardReportOpen,
  handleSubmit,
  handleSaveDraft,
  handleCancelAddAddendum,
  handleDiscardReport,
  handleOpenDiscardReportModal,
  handleKeepDraft,
  toggleFocusMode,
  onDialogActionSuccess,
  onDialogActionError,
  measureReportExitActionStartWithMetadata,
  fieldsUpdateDebounced,
  apolloQueryContext,
}: UseReporterInputBindingsParams): void => {
  const handleDraftAction = useCallback(async () => {
    if (discardReportOpen) {
      await fieldsUpdateDebounced.flush();
      handleKeepDraft();
      return;
    }
    if (unsignedReportDialogOpen) {
      handleSaveDraft({
        onSuccess: onDialogActionSuccess,
        onError: onDialogActionError,
      });
      return;
    }
    handleSaveDraft();
  }, [
    discardReportOpen,
    unsignedReportDialogOpen,
    fieldsUpdateDebounced,
    handleKeepDraft,
    handleSaveDraft,
    onDialogActionSuccess,
    onDialogActionError,
  ]);

  // The user can submit the report with a voice command, keyboard shortcut, or gamepad button
  // voice command:
  useRecorderVoiceCommandEventsListener(({ payload }) => {
    if (isLinkCasesMutating && isLinkCasesBlockedVoiceAction(payload.substitution.action)) {
      return;
    }

    if (payload.substitution.action === 'SubmitReport') {
      measureReportExitActionStartWithMetadata({
        exitAction: ReportExitAction.Sign,
        inputMethod: ReportExitActionInputMethod.VoiceCommand,
        hasConfirmationDialogTriggered: false,
      });
      if (unsignedReportDialogOpen) {
        handleSubmit({
          onSuccess: onDialogActionSuccess,
          onError: onDialogActionError,
        });
      } else {
        handleSubmit();
      }
    }

    if (payload.substitution.action === 'DraftReport') {
      handleDraftAction();
    }

    if (payload.substitution.action === 'DiscardReport') {
      if (status === ReportStatus.AddendumAdd) {
        handleCancelAddAddendum();
      } else if (discardReportOpen === true || unsignedReportDialogOpen === true) {
        // Second press while the dialog is open intentionally maps to Discard (destructive) —
        // preserves the pre-tri-state muscle memory. Keep Draft is mouse-only.
        handleDiscardReport({
          onSuccess: onDialogActionSuccess,
          onError: onDialogActionError,
        });
      } else {
        handleOpenDiscardReportModal();
      }
    }
  });

  // keyboard shortcut:
  useCrossTabEventsListener(NameSpaces.REPORTER_SHORTCUT, ({ payload }) => {
    if (isLinkCasesMutating && isLinkCasesBlockedShortcutAction(payload.action)) {
      return;
    }

    if (payload.action === 'submitReport') {
      measureReportExitActionStartWithMetadata({
        exitAction: ReportExitAction.Sign,
        inputMethod: ReportExitActionInputMethod.Keyboard,
        hasConfirmationDialogTriggered: false,
      });
      if (unsignedReportDialogOpen) {
        handleSubmit({
          onSuccess: onDialogActionSuccess,
          onError: onDialogActionError,
        });
      } else {
        handleSubmit();
      }
    }

    if (payload.action === 'draftReport') {
      handleDraftAction();
    }

    if (payload.action === 'discardReport') {
      if (status === ReportStatus.AddendumAdd) {
        handleCancelAddAddendum();
      } else if (discardReportOpen === true || unsignedReportDialogOpen === true) {
        // Second press while the dialog is open intentionally maps to Discard (destructive) —
        // preserves the pre-tri-state muscle memory. Keep Draft is mouse-only.
        handleDiscardReport({
          onSuccess: onDialogActionSuccess,
          onError: onDialogActionError,
        });
      } else {
        handleOpenDiscardReportModal();
      }
    }
  });

  // custom event:
  useEffect(() => {
    const handler = (event: CustomEvent<string>) => {
      const { detail } = event;
      if (isLinkCasesMutating && isLinkCasesBlockedExtensionShortcutDetail(detail)) {
        return;
      }

      if (
        detail === 'reporter-submit-report' &&
        status !== ReportStatus.ProvisionalSubmit &&
        status !== ReportStatus.ProvisionalAddendumSubmit
      ) {
        measureReportExitActionStartWithMetadata({
          exitAction: ReportExitAction.Sign,
          inputMethod: ReportExitActionInputMethod.Keyboard,
          hasConfirmationDialogTriggered: false,
        });
        handleSubmit();
      }

      if (detail === 'reporter-discard-report') {
        if (status === ReportStatus.AddendumAdd) {
          handleCancelAddAddendum();
        } else if (discardReportOpen === true || unsignedReportDialogOpen === true) {
          // Second press while the dialog is open intentionally maps to Discard (destructive) —
          // preserves the pre-tri-state muscle memory. Keep Draft is mouse-only.
          handleDiscardReport({
            onSuccess: onDialogActionSuccess,
            onError: onDialogActionError,
          });
        } else {
          handleOpenDiscardReportModal();
        }
      }
    };

    addExtensionListener(
      EXTENSION_RESPONSE_EVENTS.SHORTCUT_TRIGGERED,
      handler as unknown as EventListener
    );
    return () =>
      removeExtensionListener(
        EXTENSION_RESPONSE_EVENTS.SHORTCUT_TRIGGERED,
        handler as unknown as EventListener
      );
  }, [
    discardReportOpen,
    handleCancelAddAddendum,
    handleDiscardReport,
    handleOpenDiscardReportModal,
    handleSubmit,
    onDialogActionError,
    onDialogActionSuccess,
    status,
    unsignedReportDialogOpen,
    measureReportExitActionStartWithMetadata,
    isLinkCasesMutating,
  ]);

  // gamepad button:
  const gamepadCallback = useCallback(
    (params: { action: GamepadActionId; pressed: boolean }) => {
      const { action, pressed } = params;
      if (
        isLinkCasesMutating &&
        pressed &&
        [
          GamepadActionId.SubmitReport,
          GamepadActionId.DraftReport,
          GamepadActionId.SubmitPrelimReport,
        ].includes(action)
      ) {
        return;
      }

      const isProvisionalSubmittingStatus =
        status === ReportStatus.ProvisionalSubmit ||
        status === ReportStatus.ProvisionalAddendumSubmit ||
        status === ReportStatus.ProvisionalPrelimSubmit;

      if (action === GamepadActionId.SubmitReport && pressed && !isProvisionalSubmittingStatus) {
        measureReportExitActionStartWithMetadata({
          exitAction: ReportExitAction.Sign,
          inputMethod: ReportExitActionInputMethod.ControlInputDevice,
          hasConfirmationDialogTriggered: false,
        });

        handleSubmit();
        return;
      }

      if (action === GamepadActionId.DraftReport && pressed) {
        handleSaveDraft();
        return;
      }

      if (action === GamepadActionId.GenerateImpression && pressed && isAutoImpressionsEnabled) {
        sendCrossTabEvent(NameSpaces.REPORTER_SHORTCUT, {
          type: 'gamepad',
          action: 'generateImpression',
          pressed: true,
        });
        return;
      }

      if (
        action === GamepadActionId.SubmitPrelimReport &&
        pressed &&
        isPrelimSubmissionEnabled &&
        !isProvisionalSubmittingStatus
      ) {
        measureReportExitActionStartWithMetadata({
          exitAction: ReportExitAction.Prelim,
          inputMethod: ReportExitActionInputMethod.ControlInputDevice,
          hasConfirmationDialogTriggered: false,
        });

        handleSubmit({ type: SubmitType.Prelim });
        return;
      }

      if (action === GamepadActionId.ToggleVisibility && pressed) {
        dispatchExtensionEvent(focusEvent());
        return;
      }

      if (action === GamepadActionId.ToggleFocusMode && pressed && isFocusModeAllowed) {
        toggleFocusMode({ method: FocusModeToggleMethod.Dictaphone });
      }
    },
    [
      status,
      handleSubmit,
      handleSaveDraft,
      toggleFocusMode,
      isFocusModeAllowed,
      isAutoImpressionsEnabled,
      isPrelimSubmissionEnabled,
      isLinkCasesMutating,
      measureReportExitActionStartWithMetadata,
    ]
  );

  useGamepadBindings({ callback: gamepadCallback, context: apolloQueryContext });
  useWebHIDBindings({ callback: gamepadCallback });
};
